import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { dashboardGet } from '../../api/index.js'
import { authSelector } from '../../reducers/index.js'
import { localized, lowerCaseFirstLetter } from '../utils.jsx'
import {
	DashboardHeaderContainer,
	DashboardHeaderInfo,
	DashboardHeaderLogo,
	HeadingName,
	TodayDate,
} from './dashboardheader.styled.jsx'

function joinNames(names, t) {
	if (!names || names.length === 0) return null
	if (names.length === 1) return names[0]

	const allButLast = names.slice(0, -1).join(', ')
	const last = names[names.length - 1]
	return `${allButLast} ${t('and')} ${last}`
}

export const DashboardHeader = () => {
	const { t } = useTranslation()
	const { fullName, companyName, role } = useSelector(authSelector)
	const dashboardQuery = useQuery({
		queryKey: ['dashboard'],
		queryFn: () => dashboardGet().then((res) => res.data),
	})

	const localDateTime = DateTime.local().setLocale(localized.resolvedLanguage) // localWeekNumber

	const userTitle =
		{
			SYSTEM: 'system',
			ADMIN: lowerCaseFirstLetter(t('RoleAdmin')),
			HRUSER: lowerCaseFirstLetter(t('RoleHRUser')),
			CLERK: lowerCaseFirstLetter(t('RolePayrollClerk')),
			SUPERVISOR: lowerCaseFirstLetter(t('RoleSupervisor')),
			EQUIPMENT: lowerCaseFirstLetter(t('RoleEquipmentManagement')),
			EMPLOYEE: lowerCaseFirstLetter(t('RoleEmployee')),
		}[role] ?? ''

	const namedays = dashboardQuery.data ?? null
	const namedaysMemo = useMemo(() => joinNames(namedays, t), [namedays, t]) // do not re-render dashboard on every tick

	const greeting = () => {
		const h = localDateTime.hour
		if (h < 4) return t('GreetMidnight', 'Hyvää keskiyötä') // "✨ " +
		if (h < 6) return t('GreetSmallHours', 'Hyvää aamuyötä') //  "🌙 " +
		if (h < 9) return t('GreetMorning', 'Hyvää huomenta') // "☕ " +
		if (h < 11) return t('GreetForenoon', 'Mukavaa aamupäivää') //  "☀️ " +
		if (h < 12) return t('GreetLunch', 'Maistuvaa lounasta') // "🍽️ " +
		if (h < 13) return t('GreetNoon', 'Mukavaa keskipäivää') // "☀️ " +
		if (h < 18) return t('GreetAfternoon', 'Iloista iltapäivää') // "☀️ " +
		if (h < 22) return t('GreetEvening', 'Mukavaa iltaa') // "🌇 " +
		if (h < 23) return t('GreetLateEvening', 'Rentouttavaa myöhäisiltaa') // "🌙 " +
		return t('GreetNight', 'Hyvää yötä') // "✨ " +
	}
	return (
		<DashboardHeaderContainer>
			<DashboardHeaderLogo />
			<DashboardHeaderInfo>
				<div>
					<HeadingName>{companyName}</HeadingName>
					{greeting()} <strong>{fullName.replace(/ .*/, '')}</strong> ({userTitle})
				</div>
				<div>
					{t('TodayIs', 'Tänään on')}
					<TodayDate>
						{localDateTime.toLocaleString()}
						{', '}
						{t('week', 'viikko')} {localDateTime.weekNumber}
					</TodayDate>
				</div>
				{namedaysMemo && (
					<div>
						{t('NamedaysToday', 'Nimipäivää viettää tänään')}
						<TodayDate>{namedaysMemo}</TodayDate>
					</div>
				)}
			</DashboardHeaderInfo>
		</DashboardHeaderContainer>
	)
}
